import {Async, Empty, isLoaded, Loaded} from 'Core/models/Async';
import {createValidatableEmptyEmail, ValidatableEmail} from 'Core/models/Email';
import {EmptyString} from 'Core/models/EmptyString';
import {createValidatableEmptyString, ValidatableNonEmptyString} from 'Core/models/ValidatableNonEmptyString';
import {createValidatableEmptyPhone, ValidatablePhone} from 'Core/models/PhoneNumber';
import {none, Option, some} from 'fp-ts/lib/Option';
import {Lens} from 'monocle-ts';
import {EmptyRecruiterCode} from 'Agent/Policies/models/RecruiterCode';
import {ValidRegionCode} from 'Agent/Policies/models/RegionCode';
import {createAction} from 'Shared/utilities/actionCreator';
import {
  FieldSalesLeader,
  UnvalidatedFieldSalesLeader,
  validateFieldSalesLeader,
  ValidFieldSalesLeader,
} from 'Users/models/FieldSalesLeader';

export enum FieldSalesLeaderUserActionType {
  LOAD_FSL_USER = 'fieldSalesLeaderUser/LOAD_FSL_USER ',
  CHANGE_USER = 'fieldSalesLeaderUser/CHANGE',
  SAVE_FSL_USER = 'fieldSalesLeaderUser/SAVE_FSL_USER',
  FETCH_FSL_USER = 'fieldSalesLeaderUser/FETCH_FSL_USER',
  VALIDATE_USERNAME = 'fieldSalesLeaderUser/VALIDATE_USERNAME',
  SET_USERNAME = 'fieldSalesLeaderUser/SET_USERNAME',
  VALIDATE_EMAIL = 'fieldSalesLeaderUser/VALIDATE_EMAIL',
  SET_EMAIL = 'fieldSalesLeaderUser/SET_EMAIL',
  VALIDATE_PHONE = 'fieldSalesLeaderUser/VALIDATE_PHONE',
  SET_PHONE = 'fieldSalesLeaderUser/SET_PHONE',
  GET_REGIONS = 'fieldSalesLeaderUser/GET_REGIONS',
  CHANGE_REGIONS = 'fieldSalesLeaderUser/CHANGE_REGIONS',
}

export const getRegions = () => createAction(FieldSalesLeaderUserActionType.GET_REGIONS);
export const changeRegions = (regions: Async<ValidRegionCode[]>) =>
  createAction(FieldSalesLeaderUserActionType.CHANGE_REGIONS, regions);
export const validatePhone = (phone: string) => createAction(FieldSalesLeaderUserActionType.VALIDATE_PHONE, phone);
export const setPhone = (phone: ValidatablePhone) => createAction(FieldSalesLeaderUserActionType.SET_PHONE, phone);
export const validateEmail = (email: string) => createAction(FieldSalesLeaderUserActionType.VALIDATE_EMAIL, email);
export const setEmail = (email: ValidatableEmail) => createAction(FieldSalesLeaderUserActionType.SET_EMAIL, email);
export const validateUsername = (username: string) =>
  createAction(FieldSalesLeaderUserActionType.VALIDATE_USERNAME, username);
export const setUsername = (username: ValidatableNonEmptyString) =>
  createAction(FieldSalesLeaderUserActionType.SET_USERNAME, username);
export const fetchFieldSalesLeader = (userId: string) =>
  createAction(FieldSalesLeaderUserActionType.FETCH_FSL_USER, {userId});
export const loadFSLUser = (fsl: Async<FieldSalesLeader>) =>
  createAction(FieldSalesLeaderUserActionType.LOAD_FSL_USER, fsl);
export const clearFieldSalesLeader = () =>
  createAction(FieldSalesLeaderUserActionType.CHANGE_USER, DEFAULT_STATE.fieldSalesLeader);
export const changeFieldSalesLeaderUser = (fieldSalesLeader: Async<FieldSalesLeader>) =>
  createAction(FieldSalesLeaderUserActionType.CHANGE_USER, fieldSalesLeader);
export const saveFieldSalesLeaderUser = (fsl: ValidFieldSalesLeader, id?: string) =>
  createAction(FieldSalesLeaderUserActionType.SAVE_FSL_USER, {fsl, id});

type FieldSalesLeaderUserState = {
  fieldSalesLeader: Async<FieldSalesLeader>;
  originalFieldSalesLeader: Option<Async<FieldSalesLeader>>;
  regions: Async<ValidRegionCode[]>;
};

type FieldSalesLeaderUserAction =
  | ReturnType<typeof changeRegions>
  | ReturnType<typeof setUsername>
  | ReturnType<typeof setPhone>
  | ReturnType<typeof setEmail>
  | ReturnType<typeof changeFieldSalesLeaderUser>
  | ReturnType<typeof loadFSLUser>;

export const DEFAULT_STATE = {
  fieldSalesLeader: new Loaded(
    new UnvalidatedFieldSalesLeader(
      new EmptyString(),
      new EmptyString(),
      createValidatableEmptyString(),
      createValidatableEmptyPhone(),
      createValidatableEmptyEmail(),
      EmptyRecruiterCode,
      false,
      false
    )
  ),
  originalFieldSalesLeader: none,
  regions: new Empty(),
};

const fslLens = Lens.fromProp<FieldSalesLeader>();

export default (
  state: FieldSalesLeaderUserState = DEFAULT_STATE,
  action: FieldSalesLeaderUserAction
): FieldSalesLeaderUserState => {
  switch (action.type) {
    case FieldSalesLeaderUserActionType.CHANGE_REGIONS:
      return {...state, regions: action.payload};
    case FieldSalesLeaderUserActionType.SET_EMAIL: {
      return {
        ...state,
        fieldSalesLeader: isLoaded(state.fieldSalesLeader)
          ? new Loaded(validateFieldSalesLeader(fslLens('email').set(action.payload)(state.fieldSalesLeader.Data)))
          : state.fieldSalesLeader,
      };
    }
    case FieldSalesLeaderUserActionType.SET_USERNAME: {
      return {
        ...state,
        fieldSalesLeader: isLoaded(state.fieldSalesLeader)
          ? new Loaded(validateFieldSalesLeader(fslLens('username').set(action.payload)(state.fieldSalesLeader.Data)))
          : state.fieldSalesLeader,
      };
    }
    case FieldSalesLeaderUserActionType.SET_PHONE: {
      return {
        ...state,
        fieldSalesLeader: isLoaded(state.fieldSalesLeader)
          ? new Loaded(
              validateFieldSalesLeader(fslLens('phoneNumber').set(action.payload)(state.fieldSalesLeader.Data))
            )
          : state.fieldSalesLeader,
      };
    }
    case FieldSalesLeaderUserActionType.LOAD_FSL_USER:
      return {
        ...state,
        fieldSalesLeader: action.payload,
        originalFieldSalesLeader: some(action.payload),
      };
    case FieldSalesLeaderUserActionType.CHANGE_USER:
      return {
        ...state,
        fieldSalesLeader: action.payload,
      };
    default:
      return state;
  }
};
